import React, { useEffect } from "react";
import {
  BrowserRouter,
  Routes,
  Route,
  Navigate,
  useLocation,
} from "react-router-dom";
import { Provider } from "react-redux";
import { store } from "./redux/store";
import { ToastContainer } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

// Pages
import Login from "./pages/Login";
import Home from "./pages/Home";
import Header from "./pages/Header";
import NotFound from "./pages/NotFound";

// Student
import StudentDetails from "./pages/student/StudentDetails";
import AddStudent from "./pages/student/AddStudent";
import UpdateStudent from "./pages/student/UpdateStudent";
import LeftStudents from "./pages/student/LeftStudents";
import InactiveStudents from "./pages/student/InactiveStudents";
import Scholarship from "./pages/student/Scholarship";
import UpdateTransport from "./pages/student/UpdateTransport";
import UpdateScholarship from "./pages/student/UpdateScholarship";
import ViewStudent from "./pages/student/ViewStudent";

// HR
import StaffDirectory from "./pages/hr/StaffDirectory";
import StaffAttendance from "./pages/hr/StaffAttendance";
import StaffAppliedLeave from "./pages/hr/StaffAppliedLeave";
import AddStaff from "./pages/hr/AddStaff";
import LeaveRequest from "./pages/hr/LeaveRequest";
import TeachersRating from "./pages/hr/TeachersRating";
import ViewStaff from "./pages/hr/ViewStaff";
import UpdateStaff from "./pages/hr/UpdateStaff";
import UpdateStaffTransport from "./pages/hr/UpdateStaffTransport";

// Fee
import CollectFees from "./pages/fee/CollectFees";
import AdmissionForm from "./pages/fee/AdmissionForm";
import SellAdmissionForm from "./pages/fee/SellAdmissionForm";
import MiscellaneousFees from "./pages/fee/MiscellaneousFees";
import DuesFee from "./pages/fee/DuesFee";
import PaidFee from "./pages/fee/PaidFee";
import MonthlyFees from "./pages/fee/MonthlyFees";
import FeesMaster from "./pages/fee/FeesMaster";
import CollectedBySummary from "./pages/fee/CollectedBySummary";
import CheckPaymentStatus from "./pages/fee/CheckPaymentStatus";

// Transport
import BusDetails from "./pages/transport/BusDetails";
import AddBus from "./pages/transport/AddBus";
import DriverDetails from "./pages/transport/DriverDetails";
import AddDriver from "./pages/transport/AddDriver";
import LeftDrivers from "./pages/transport/LeftDrivers";
import BusStoppage from "./pages/transport/BusStoppage";
import BusRoute from "./pages/transport/BusRoute";
import TrackBus from "./pages/transport/TrackBus";
import UpdateBus from "./pages/transport/UpdateBus";
import LeftBuses from "./pages/transport/LeftBuses";
import UpdateDriver from "./pages/transport/UpdateDriver";
import StudentTransportDetails from "./pages/transport/StudentTransportDetails";
import BusTiming from "./pages/transport/BusTiming";

// Front Office
import AdmissionEnquiry from "./pages/front-office/AdmissionEnquiry";
import VisitorBook from "./pages/front-office/VisitorBook";
import ComplainBox from "./pages/front-office/ComplainBox";
import GatePass from "./pages/front-office/GatePass";
import MedicalAlert from "./pages/front-office/MedicalAlert";
import ViewStudentDetails from "./pages/student/ViewStudentDetails";

// Behaviour
import Incidents from "./pages/behaviour-records/Incidents";
import AssignViewIncidents from "./pages/behaviour-records/AssignViewIncidents";

// Communication
import NoticeBoard from "./pages/communicate/NoticeBoard";
import SendEmail from "./pages/communicate/SendEmail";
import SendPushNotification from "./pages/communicate/SendPushNotification";
import DFSMessage from "./pages/communicate/DFSMessage";
import SendSMS from "./pages/communicate/SendSMS";
import SMSTemplate from "./pages/communicate/SMSTemplate";
import Holidays from "./pages/communicate/Holidays";

// Admin
import UserDetails from "./pages/admin/UserDetails";
import Settings from "./pages/Settings";
import LiveEvent from "./pages/admin/LiveEvent";

// Expense
import ExpenseDetails from "./pages/expense/ExpenseDetails";
import RoleProtectedRoute from "./RoleProtectedRoute";
import Homework from "./pages/homework/Homework";

// Attendance
import SchoolAttendance from "./pages/attendance/SchoolAttendance";
import BusAttendance from "./pages/attendance/BusAttendance";
import BusWiseAttendance from "./pages/attendance/BusWiseAttendance";
import AppliedLeave from "./pages/attendance/AppliedLeave";
import ClassSectionAttendance from "./pages/attendance/ClassSectionAttendance";
import StudentAttendance from "./pages/attendance/StudentAttendance";
import BusAttendanceStatistics from "./pages/attendance/BusAttendanceStatistics";
import SchoolAttendanceStatistics from "./pages/attendance/SchoolAttendanceStatistics";

// Library
import BookList from "./pages/library/BookList";
import AddBook from "./pages/library/AddBook";
import UpdateBook from "./pages/library/UpdateBook";
import EBookList from "./pages/library/EBookList";
import BookIssueReturnStudent from "./pages/library/BookIssueReturnStudent";

// Examinations
import Syllabus from "./pages/examinations/Syllabus";
import SamplePaper from "./pages/examinations/SamplePaper";
import HomeworkStatistics from "./pages/homework/HomeworkStatistics";
import LeftStaff from "./pages/hr/LeftStaff";
import AdmitCard from "./pages/examinations/AdmitCard";
import AddExaminationDetails from "./pages/examinations/AddExaminationDetails";
import UpdateExaminationDetails from "./pages/examinations/UpdateExaminationDetails";
import ExaminationMarks from "./pages/examinations/ExaminationMarks";
import Result from "./pages/examinations/Result";
import Toppers from "./pages/examinations/Toppers";
import ManageVendors from "./pages/vendor/ManageVendors";
import ContractsRenewals from "./pages/vendor/ContractsRenewals";
import PaymentTracking from "./pages/vendor/PaymentTracking";

// Reports
import StudentReports from "./pages/reports/Students";
import FinanceReports from "./pages/reports/Finance";
import AttendanceReports from "./pages/reports/Attendance";
import ExaminationReports from "./pages/reports/Examination";
import HomeworkReports from "./pages/reports/Homework";
import StaffReports from "./pages/reports/Staffs";
import FrontOfficeReports from "./pages/reports/FrontOffice";
import LibraryReports from "./pages/reports/Library";
import TransportReports from "./pages/reports/Transport";

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
}

const ProtectedRoute = ({ children }) => {
  const token = localStorage.getItem("token");
  return token ? children : <Navigate to="/" replace />;
};

function Layout() {
  const location = useLocation();
  const hideHeaderRoutes = ["/", "/not-found"];

  return (
    <>
      {!hideHeaderRoutes.includes(location.pathname) && <Header />}
      <ToastContainer position="top-right" autoClose={3000} />
      <ScrollToTop />
      <Routes>
        <Route path="/" element={<Login />} />
        <Route path="/not-found" element={<NotFound />} />

        <Route
          path="/home"
          element={
            <ProtectedRoute>
              <Home />
            </ProtectedRoute>
          }
        />

        <Route
          path="/gate-pass"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Front Office">
                <GatePass />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/medical-alert"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Front Office">
                <MedicalAlert />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admission-enquiry"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Front Office">
                <AdmissionEnquiry />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/visitor-book"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Front Office">
                <VisitorBook />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/view-student-details"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Front Office, Principal">
                <ViewStudentDetails />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/student-details"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Student Information, Principal">
                <StudentDetails />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/left-students"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Student Information, Principal">
                <LeftStudents />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/inactive-students"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Student Information, Principal">
                <InactiveStudents />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-student"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Student Information, Principal">
                <AddStudent />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/update-student"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Student Information, Principal">
                <UpdateStudent />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/view-student"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Student Information, Transport Management, Front Office, Principal">
                <ViewStudent />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/update-transport"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Student Information, Transport Management">
                <UpdateTransport />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/update-scholarship"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Student Information">
                <UpdateScholarship />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/scholarship"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Student Information">
                <Scholarship />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/incidents"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Behaviour Records">
                <Incidents />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/assign-view-incidents"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Behaviour Records">
                <AssignViewIncidents />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/add-staff"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Human Resource">
                <AddStaff />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/left-staff"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Human Resource, Principal">
                <LeftStaff />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/update-staff"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Human Resource">
                <UpdateStaff />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff-directory"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Human Resource, Transport Management, Principal">
                <StaffDirectory />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff-attendance"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Human Resource, Principal">
                <StaffAttendance />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/staff-applied-leave"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Human Resource, Principal">
                <StaffAppliedLeave />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/view-staff"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Human Resource, Principal">
                <ViewStaff />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/update-staff-transport"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Human Resource, Transport Management">
                <UpdateStaffTransport />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/leave-request"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Human Resource, Principal">
                <LeaveRequest />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/notice-board"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Communicate, Principal">
                <NoticeBoard />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/send-dfs-message"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Communicate, Principal">
                <DFSMessage />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/send-email"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Communicate, Principal">
                <SendEmail />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/send-push-notification"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Communicate, Principal">
                <SendPushNotification />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/send-sms"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Communicate, Principal">
                <SendSMS />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sms-template"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Communicate, Principal">
                <SMSTemplate />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/holidays"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Communicate, Principal">
                <Holidays />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/fees-master"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Fees Collection">
                <FeesMaster />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/monthly-fees"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Fees Collection">
                <MonthlyFees />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/collect-fees"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Fees Collection">
                <CollectFees />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admission-form"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Fees Collection">
                <AdmissionForm />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/miscellaneous-fees"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Fees Collection">
                <MiscellaneousFees />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sell-admission-form"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Fees Collection">
                <SellAdmissionForm />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/dues-fee"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Fees Collection">
                <DuesFee />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/paid-fee"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Fees Collection">
                <PaidFee />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/collectedby-summary"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Fees Collection">
                <CollectedBySummary />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/check-payment-status"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Fees Collection">
                <CheckPaymentStatus />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/bus-details"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Transport Management">
                <BusDetails />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/track-bus"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Transport Management">
                <TrackBus />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-bus"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Transport Management">
                <AddBus />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/left-buses"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Transport Management">
                <LeftBuses />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/update-bus"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Transport Management">
                <UpdateBus />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/driver-details"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Transport Management">
                <DriverDetails />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-driver"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Transport Management">
                <AddDriver />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/left-drivers"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Transport Management">
                <LeftDrivers />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/update-driver"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Transport Management">
                <UpdateDriver />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-stoppage"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Transport Management">
                <BusStoppage />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-route"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Transport Management">
                <BusRoute />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-timing"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Transport Management">
                <BusTiming />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/student-transport-details"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Transport Management">
                <StudentTransportDetails />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/expense-details"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Expenses">
                <ExpenseDetails />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/user-details"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Admin Users">
                <UserDetails />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/live-event"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Admin Users">
                <LiveEvent />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/teachers-rating"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Admin Users">
                <TeachersRating />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/complain-box"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Admin Users">
                <ComplainBox />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/homework"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Homework">
                <Homework />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/homework-statistics"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Homework">
                <HomeworkStatistics />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/school-attendance"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Attendance">
                <SchoolAttendance />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-attendance"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Attendance, Transport Management">
                <BusAttendance />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-attendance-statistics"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Attendance, Transport Management">
                <BusAttendanceStatistics />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/applied-leave"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Attendance">
                <AppliedLeave />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/class-section-attendance"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Attendance">
                <ClassSectionAttendance />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/bus-wise-attendance"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Attendance, Transport Management">
                <BusWiseAttendance />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/student-attendance"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Attendance">
                <StudentAttendance />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/school-attendance-statistics"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Attendance">
                <SchoolAttendanceStatistics />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/settings"
          element={
            <ProtectedRoute>
              <Settings />
            </ProtectedRoute>
          }
        />

        <Route
          path="/book-list"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Library">
                <BookList />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-book"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Library">
                <AddBook />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/update-book"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Library">
                <UpdateBook />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/e-book-list"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Library">
                <EBookList />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/book-issue-return-student"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Library">
                <BookIssueReturnStudent />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/syllabus"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Examinations, Principal">
                <Syllabus />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/sample-paper"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Examinations, Principal">
                <SamplePaper />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/admit-card"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Examinations, Principal">
                <AdmitCard />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/examination-marks"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Examinations, Principal">
                <ExaminationMarks />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/add-examination-details"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Examinations, Principal">
                <AddExaminationDetails />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/update-examination-details"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Examinations, Principal">
                <UpdateExaminationDetails />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/result"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Examinations, Principal">
                <Result />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/toppers"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Examinations, Principal">
                <Toppers />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/vendors"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Vendor">
                <ManageVendors />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/contracts-renewals"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Vendor">
                <ContractsRenewals />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/payment-tracking"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Vendor">
                <PaymentTracking />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route
          path="/reports/students"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Reports">
                <StudentReports />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/finance"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Reports">
                <FinanceReports />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/attendance"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Reports">
                <AttendanceReports />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/examination"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Reports">
                <ExaminationReports />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/homework"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Reports">
                <HomeworkReports />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/staffs"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Reports">
                <StaffReports />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/front-office"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Reports">
                <FrontOfficeReports />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/library"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Reports">
                <LibraryReports />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />
        <Route
          path="/reports/transport"
          element={
            <ProtectedRoute>
              <RoleProtectedRoute module="Reports">
                <TransportReports />
              </RoleProtectedRoute>
            </ProtectedRoute>
          }
        />

        <Route path="*" element={<Navigate to="/not-found" />} />
      </Routes>
    </>
  );
}

function App() {
  return (
    <Provider store={store}>
      <BrowserRouter>
        <Layout />
      </BrowserRouter>
    </Provider>
  );
}

export default App;
